import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, Download } from "lucide-react";
import axios from "axios";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", title: null, token: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error" });
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setState({ phase: "paid", title: res.data.title, token: res.data.download_token });
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      attempts += 1;
      if (attempts < 12) setTimeout(poll, 2000);
      else setState({ phase: "timeout" });
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-void text-white flex items-center justify-center px-4 hero-grid">
      <Seo title="Zahlung · SCHATTENPSYCHE" siteName="SCHATTENPSYCHE" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="glass rounded-2xl p-10 max-w-md w-full text-center"
        data-testid="payment-success-page"
      >
        <img src="/logo.png" alt="Schattenpsyche Logo" className="w-16 h-16 rounded-full mx-auto mb-6 ring-1 ring-white/20" />
        {state.phase === "checking" && (
          <>
            <Loader2 className="w-10 h-10 text-white animate-spin mx-auto mb-4" />
            <h1 className="font-cinzel text-2xl font-bold">Zahlung wird geprüft…</h1>
            <p className="text-slate-400 text-sm mt-3">Einen Moment, wir bestätigen deine Bestellung.</p>
          </>
        )}
        {state.phase === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-white mx-auto mb-4" />
            <span className="stamp text-xs mb-4">Akte geschlossen</span>
            <h1 className="font-cinzel text-2xl font-bold mt-4" data-testid="payment-confirmed-heading">
              Zahlung bestätigt
            </h1>
            <p className="text-slate-400 text-sm mt-3">
              {state.title ? `Dein Exemplar von ${state.title} ist freigegeben.` : "Deine Bestellung ist bestätigt."}{" "}
              Dein Download-Link wurde per E-Mail an dich gesendet.
            </p>
            {state.token && (
              <Link
                to={`/download/${state.token}`}
                data-testid="payment-download-button"
                className="glow-btn mt-6 !py-3 text-sm w-full"
              >
                <Download className="w-4 h-4" />
                Direkt zum Download
              </Link>
            )}
          </>
        )}
        {state.phase === "timeout" && (
          <>
            <Loader2 className="w-10 h-10 text-paper animate-spin mx-auto mb-4" />
            <h1 className="font-cinzel text-2xl font-bold">Noch in Bearbeitung</h1>
            <p className="text-slate-400 text-sm mt-3">
              Deine Zahlung wird gerade bestätigt. Du erhältst in Kürze eine E-Mail mit deinem Download-Link.
            </p>
          </>
        )}
        {state.phase === "error" && (
          <>
            <XCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h1 className="font-cinzel text-2xl font-bold">Sitzung nicht gefunden</h1>
            <p className="text-slate-400 text-sm mt-3">Wir konnten diese Kaufabwicklung nicht finden.</p>
          </>
        )}
        <Link
          to="/"
          data-testid="payment-success-home-link"
          className="inline-block mt-8 font-mono text-xs uppercase tracking-[0.25em] text-slate-500 hover:text-white transition-colors"
        >
          Zurück zum Archiv
        </Link>
      </motion.div>
    </div>
  );
}
